import { backendFetch, BackendError } from "@/lib/backend";
import type {
  ProjectCreateRequest,
  ProjectUpdateRequest,
  DeepResearchResultResponse,
  ProjectListItem,
  ProjectWithRFIResponse,
  RFICreate,
} from "@/types/backend";

async function readBodyTextSafe(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

function extractBackendDetailMessage(bodyText: string): string | null {
  const text = bodyText.trim();
  if (!text) return null;

  try {
    const parsed: unknown = JSON.parse(text);
    if (!parsed || typeof parsed !== "object") return null;

    const detail = (parsed as any).detail as unknown;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d) => (d && typeof d === "object" ? (d as any).msg : null))
        .filter((m): m is string => typeof m === "string" && m.trim().length > 0)
        .map((m) => m.trim());
      if (msgs.length > 0) return msgs.join("\n");
    }

    const message = (parsed as any).message;
    if (typeof message === "string" && message.trim()) return message.trim();
  } catch {
    // not JSON
  }

  return null;
}

/**
 * Creates a project (without RFI).
 * POST /api/projects with ProjectCreateRequest body.
 */
export async function createProject(
  request: ProjectCreateRequest
): Promise<ProjectListItem> {
  const response = await backendFetch("/api/projects", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
  });

  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const bodyText = await readBodyTextSafe(response);
      throw new BackendError("Failed to create project (unexpected response).", {
        status: response.status,
        bodyText,
      });
    }
    return (await response.json()) as ProjectListItem;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to create project.", {
    status: response.status,
    bodyText,
  });
}

/**
 * Updates a project's metadata.
 * PATCH /api/projects/{project_id} with ProjectUpdateRequest body.
 */
export async function updateProject(
  projectId: string,
  request: ProjectUpdateRequest
): Promise<ProjectListItem> {
  const response = await backendFetch(`/api/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
  });

  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const bodyText = await readBodyTextSafe(response);
      throw new BackendError("Failed to update project (unexpected response).", {
        status: response.status,
        bodyText,
      });
    }
    return (await response.json()) as ProjectListItem;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to update project.", {
    status: response.status,
    bodyText,
  });
}

/**
 * Creates RFI for a project.
 * POST /api/projects/{project_id}/rfi with RFICreate body.
 */
export async function createRfi(
  projectId: string,
  rfiData: RFICreate
): Promise<RFICreate> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/rfi`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(rfiData),
    }
  );

  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const bodyText = await readBodyTextSafe(response);
      throw new BackendError("Failed to create RFI (unexpected response).", {
        status: response.status,
        bodyText,
      });
    }
    return (await response.json()) as RFICreate;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to create RFI.", {
    status: response.status,
    bodyText,
  });
}

/**
 * Lists projects for the current user.
 * GET /api/projects
 * @param archived - Optional. If true, returns only archived projects. If false, returns only non-archived projects. If undefined, returns only non-archived projects (default).
 */
export async function listProjects(archived?: boolean): Promise<ProjectListItem[]> {
  let url = "/api/projects";
  if (archived === true) {
    url += "?archived=true";
  } else if (archived === false) {
    url += "?archived=false";
  }
  
  const response = await backendFetch(url, { method: "GET" });
  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const bodyText = await readBodyTextSafe(response);
      throw new BackendError("Failed to list projects (unexpected response).", {
        status: response.status,
        bodyText,
      });
    }
    return (await response.json()) as ProjectListItem[];
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to list projects.", {
    status: response.status,
    bodyText,
  });
}

/**
 * Archives a project.
 * POST /api/projects/{project_id}/archive
 * Returns 204 No Content on success.
 */
export async function archiveProject(projectId: string): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/archive`,
    { method: "POST" }
  );

  if (response.ok) {
    // 204 No Content - no body to parse
    return;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to archive project.", {
    status: response.status,
    bodyText,
  });
}

/**
 * Unarchives a project.
 * POST /api/projects/{project_id}/unarchive
 * Returns 204 No Content on success.
 */
export async function unarchiveProject(projectId: string): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/unarchive`,
    { method: "POST" }
  );

  if (response.ok) {
    // 204 No Content - no body to parse
    return;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to unarchive project.", {
    status: response.status,
    bodyText,
  });
}

export async function getProjectWithRfi(
  projectId: string
): Promise<ProjectWithRFIResponse> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}`,
    { method: "GET" }
  );

  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const bodyText = await readBodyTextSafe(response);
      throw new BackendError("Failed to load project (unexpected response).", {
        status: response.status,
        bodyText,
      });
    }
    return (await response.json()) as ProjectWithRFIResponse;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to load project.", {
    status: response.status,
    bodyText,
  });
}

export async function getDeepResearchResult(
  projectId: string
): Promise<DeepResearchResultResponse> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/deep-research`,
    { method: "GET" }
  );

  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const bodyText = await readBodyTextSafe(response);
      throw new BackendError(
        "Failed to load Deep Research (unexpected response).",
        {
          status: response.status,
          bodyText,
        }
      );
    }
    return (await response.json()) as DeepResearchResultResponse;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to load Deep Research.", {
    status: response.status,
    bodyText,
  });
}

export async function startDeepResearch(
  projectId: string
): Promise<DeepResearchResultResponse> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/deep-research/start`,
    { method: "POST" }
  );

  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (!contentType.includes("application/json")) {
      const bodyText = await readBodyTextSafe(response);
      throw new BackendError(
        "Failed to start Deep Research (unexpected response).",
        {
          status: response.status,
          bodyText,
        }
      );
    }
    return (await response.json()) as DeepResearchResultResponse;
  }

  const bodyText = await readBodyTextSafe(response);
  const detailMessage = extractBackendDetailMessage(bodyText);
  throw new BackendError(detailMessage ?? "Failed to start Deep Research.", {
    status: response.status,
    bodyText,
  });
}


